import React, { useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import { useForm } from 'react-hook-form';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import FilterNumericRange from '../../../components/Filter/components/FilterNumericRange';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import moment from 'moment';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';
import PolygonByTypeDropdown from '../../../components/PolygonByTypeDropdown';
import { crmService } from '../../../services/central-api/crm';
import Toast from 'react-native-toast-message';
import CompanyDropdown from '../../../components/CompanyDropdown';
import BasicModal from '../../../components/BasicModal';

interface Props {
    isVisible: boolean;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}
type ValuePiece = Date | null;

interface DownloadForm {
    companyId: number;
    start: { from: ValuePiece; to: ValuePiece; };
    finish: { from: ValuePiece; to: ValuePiece; };
    onlyOffline: boolean;
    fromDuration: number;
    toDuration: number;
}

export default function CameraHistoryReportModal({ isVisible, setIsVisible }: Props) {
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>('');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    const [selectedPolygon, setSelectedPolygon] = useState<number>(0);

    const defaultEmptyValues: DownloadForm = {
        companyId: 0,
        start: { from: null, to: null },
        finish: { from: null, to: null },
        onlyOffline: false,
        fromDuration: 0,
        toDuration: 0,
    };

    const form = useForm<DownloadForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    async function formSubmit() {
        try {
            const values = form.getValues();
            const csv = await crmService.downloadReportOfflineHistory({
                ...values,
                fromStart: values.start.from ? values.start.from.getTime() : undefined,
                toStart: values.start.to ? values.start.to.getTime() : undefined,
                fromFinish: values.finish.from ? values.finish.from.getTime() : undefined,
                toFinish: values.finish.to ? values.finish.to.getTime() : undefined,
                polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
                polygon: selectedPolygon !== 0 ? selectedPolygon : undefined
            });
            if (!(csv instanceof Blob)) {
                throw new Error();
            }
            const url = window.URL.createObjectURL(csv);
            const a = document.createElement('a');
            a.href = url;
            a.download = `offline-tickets-${moment(new Date()).format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);

            form.reset(defaultEmptyValues);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <BasicModal
            isModalVisible={isVisible}
            setModalVisible={setIsVisible}
            width='small'
            title={translate('downloadReport')}
            primaryButton={{
                text: translate('download'),
                onClick: formSubmit,
                disabled: !form.watch('companyId') || polygonPendingLoading,
            }}
            cancelButton={{
                onClick: () => form.reset(defaultEmptyValues),
            }}
            disableScrollView
        >
            <View style={{ height: 620, gap: 8 }}>
                <CompanyDropdown
                    form={form}
                    fieldName='companyId'
                    required={true}
                    disabled={false}
                    zIndex={7}
                />

                <MyDateRangePicker
                    label={translate('startBetween')}
                    maxDate={moment().toDate()}
                    form={form}
                    fieldName='start'
                    zIndex={5}
                />
                <MyDateRangePicker
                    label={translate('finishBetween')}
                    maxDate={moment().toDate()}
                    form={form}
                    fieldName='finish'
                    zIndex={4}
                />

                <FilterNumericRange
                    label={`${translate('duration')}(${translate('minutes')})`}
                    onFromChange={(value) => {
                        form.setValue('fromDuration', value ?? 0);
                    }}
                    fromValue={form.watch('fromDuration')}
                    onToChange={(value) => {
                        form.setValue('toDuration', value ?? 0);
                    }}
                    toValue={form.watch('toDuration')}
                    hideClearValue={true}
                />

                <FilterCheckBox
                    label={translate('onlyOpenedTickets')}
                    value={form.watch('onlyOffline')}
                    onChange={(value) => {
                        form.setValue('onlyOffline', value);
                    }}
                />
                <PolygonTypeDropdown
                    label={translate('regions')}
                    dropDownDirection='BOTTOM'
                    showTooltip
                    tooltipText={translate('regionsInfo')}
                    zIndex={3}
                    polygonType={selectedPolygonType}
                    setPolygonType={setSelectedPolygonType}
                    requiresCompanyId={true}
                    companyId={form.getValues().companyId}
                />
                {selectedPolygonType != '' &&
                    <PolygonByTypeDropdown
                        label={translate('subRegions')}
                        dropDownDirection='BOTTOM'
                        setPolygon={setSelectedPolygon}
                        polygon={selectedPolygon}
                        polygonType={selectedPolygonType}
                        showTooltip
                        tooltipText={translate('subRegionsInfo')}
                        zIndex={2}
                        setPendingLoading={setPolygonPendingLoading}
                        requiresCompanyId={true}
                        companyId={form.getValues().companyId}
                    />
                }
            </View>
        </BasicModal>
    );
}
